import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, ErreurApi } from './api';

/**
 * Notifications push sur l'appareil (TF-460) : le navigateur s'abonne auprès
 * de son service de push avec la clé publique de la plateforme, l'API garde
 * l'abonnement et y poste les rappels d'émargement ; `public/sw.js` les
 * affiche.
 *
 * Sur iPhone, le push n'existe que pour l'application ajoutée à l'écran
 * d'accueil (iOS 16.4 et suivants) : ouverte dans Safari, la page n'a pas de
 * `PushManager`, et l'écran explique la marche à suivre.
 */

export interface EtatPush {
  disponible: boolean;
  clePublique: string | null;
  appareils: number;
}

export type EtatAppareil = 'NON_PRIS_EN_CHARGE' | 'IPHONE_A_INSTALLER' | 'BLOQUE' | 'ACTIF' | 'INACTIF';

const CHEMIN_SERVICE_WORKER = '/sw.js';

export function pushPrisEnCharge(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function surIphone(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Clé publique VAPID (base64 URL) en octets, la forme qu'attend `subscribe`. */
function octetsDeCle(cle: string): Uint8Array<ArrayBuffer> {
  const base64 = (cle + '='.repeat((4 - (cle.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const binaire = atob(base64);
  const octets = new Uint8Array(new ArrayBuffer(binaire.length));
  for (let rang = 0; rang < binaire.length; rang += 1) octets[rang] = binaire.charCodeAt(rang);
  return octets;
}

function memeCle(abonnement: PushSubscription, cle: string): boolean {
  const actuelle = abonnement.options.applicationServerKey;
  if (!actuelle) return false;
  const attendue = octetsDeCle(cle);
  const lue = new Uint8Array(actuelle);
  return lue.length === attendue.length && lue.every((octet, rang) => octet === attendue[rang]);
}

async function abonnementDeCetAppareil(): Promise<PushSubscription | null> {
  if (!pushPrisEnCharge()) return null;
  const enregistrement = await navigator.serviceWorker.getRegistration('/');
  return enregistrement ? enregistrement.pushManager.getSubscription() : null;
}

/**
 * Déconnexion : l'appareil cesse de recevoir les notifications de ce compte.
 * Sans quoi la personne suivante sur un appareil partagé recevrait celles du
 * précédent. Ne bloque jamais la déconnexion.
 */
export async function retirerCetAppareil(): Promise<void> {
  try {
    const abonnement = await abonnementDeCetAppareil();
    if (!abonnement) return;
    await appelApi('/notifications/push/abonnements/retrait', {
      methode: 'POST',
      corps: { adresse: abonnement.endpoint },
    }).catch(() => undefined);
    await abonnement.unsubscribe();
  } catch (erreur) {
    console.warn('[push] retrait de l’appareil impossible', erreur);
  }
}

/**
 * À l'ouverture d'une session : un abonnement laissé là par un autre compte
 * (session expirée sans déconnexion) est retiré de l'appareil — il cesse de
 * recevoir les notifications de cet autre compte.
 */
export async function verifierCetAppareil(): Promise<void> {
  try {
    const abonnement = await abonnementDeCetAppareil();
    if (!abonnement) return;
    const { connu } = await appelApi<{ connu: boolean }>('/notifications/push/appareil', {
      methode: 'POST',
      corps: { adresse: abonnement.endpoint },
    });
    if (!connu) await abonnement.unsubscribe();
  } catch (erreur) {
    console.warn('[push] vérification de l’appareil impossible', erreur);
  }
}

export function useEtatPush() {
  return useQuery({
    queryKey: ['notifications', 'push'],
    queryFn: () => appelApi<EtatPush>('/notifications/push'),
  });
}

/** État de cet appareil et gestes pour activer ou retirer le push. */
export function useAppareilPush(etat: EtatPush | undefined) {
  const client = useQueryClient();
  const [actif, setActif] = useState<boolean | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const relire = useCallback(async () => {
    if (!pushPrisEnCharge()) {
      setActif(false);
      return;
    }
    const abonnement = await abonnementDeCetAppareil();
    if (!abonnement) {
      setActif(false);
      return;
    }
    const { connu } = await appelApi<{ connu: boolean }>('/notifications/push/appareil', {
      methode: 'POST',
      corps: { adresse: abonnement.endpoint },
    });
    setActif(connu);
  }, []);

  useEffect(() => {
    relire().catch(() => setActif(false));
  }, [relire]);

  const etatAppareil: EtatAppareil | null = !pushPrisEnCharge()
    ? surIphone()
      ? 'IPHONE_A_INSTALLER'
      : 'NON_PRIS_EN_CHARGE'
    : Notification.permission === 'denied'
      ? 'BLOQUE'
      : actif === null
        ? null
        : actif
          ? 'ACTIF'
          : 'INACTIF';

  const activer = useCallback(async () => {
    if (!etat?.clePublique) return;
    setErreur(null);
    setEnCours(true);
    try {
      // La demande d'autorisation vient en premier : Safari ne l'accepte que
      // dans le geste de l'utilisateur.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setErreur('Notifications refusées : autorisez-les dans les réglages du navigateur pour ce site.');
        return;
      }
      const enregistrement = await navigator.serviceWorker.register(CHEMIN_SERVICE_WORKER, { scope: '/' });
      await navigator.serviceWorker.ready;
      let abonnement = await enregistrement.pushManager.getSubscription();
      // Clés de la plateforme changées : l'ancien abonnement ne sert plus.
      if (abonnement && !memeCle(abonnement, etat.clePublique)) {
        await abonnement.unsubscribe();
        abonnement = null;
      }
      abonnement ??= await enregistrement.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: octetsDeCle(etat.clePublique),
      });
      const json = abonnement.toJSON();
      await appelApi('/notifications/push/abonnements', {
        methode: 'POST',
        corps: { adresse: json.endpoint, cles: json.keys },
      });
      setActif(true);
      void client.invalidateQueries({ queryKey: ['notifications', 'push'] });
    } catch (echec) {
      console.warn('[push] activation impossible', echec);
      setErreur(
        echec instanceof ErreurApi ? echec.message : 'Les notifications n’ont pas pu être activées sur cet appareil.',
      );
    } finally {
      setEnCours(false);
    }
  }, [client, etat?.clePublique]);

  const desactiver = useCallback(async () => {
    setErreur(null);
    setEnCours(true);
    try {
      await retirerCetAppareil();
      setActif(false);
      void client.invalidateQueries({ queryKey: ['notifications', 'push'] });
    } finally {
      setEnCours(false);
    }
  }, [client]);

  return { etatAppareil, enCours, erreur, activer, desactiver };
}
